import { getAuthToken, clearToken, clearStoredSession } from './sessionStorage';

/**
 * Single API choke point (same-origin reverse proxy in the host Caddyfile).
 * Attaches the stored session token as `Authorization: Bearer <token>` when
 * one exists (go-live-readiness.md §3) — tenant identity travels ONLY in the
 * token claims; the backend derives tenantId from them. Never attach a
 * client-supplied tenant header here: it would be an isolation defect, not a
 * convenience (§0 hard constraint).
 *
 * 401 interceptor (auth-full-spec §5): an authenticated-shaped call that PB
 * rejects (expired/revoked token) must NOT leave the user stranded in the app
 * with a dead token — token + session storage are cleared and the app hard-
 * navigates to /#/login. Excluded: /api/collections/users/auth-* (the login/
 * OTP/OAuth ATTEMPT endpoints — a 401 there is a wrong-credential answer, not
 * a dead session, and hard-redirecting from the login screen would loop).
 */
const AUTH_ATTEMPT_PREFIX = '/api/collections/users/auth-';

export async function apiFetch(path, options = {}) {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(path, {
    ...options,
    headers
  });

  if (!response.ok) {
    if (
      response.status === 401 &&
      !String(path).startsWith(AUTH_ATTEMPT_PREFIX)
    ) {
      // Dead-session recovery — storage is cleared directly (sessionStorage.js
      // is a sibling lib; importing the store here would risk a circular
      // import). Hard assignment also drops any in-memory app state.
      clearToken();
      clearStoredSession();
      window.location.hash = '#/login';
    }
    throw new Error(`API request failed: ${response.status}`);
  }

  return response.json();
}
